import { RawEventStreamMessage } from 'src/core/services/eventStream/types';

/**
 * Pure SSE wire-format parser.
 *
 * Implements the WHATWG Server-Sent Events line protocol:
 * https://html.spec.whatwg.org/multipage/server-sent-events.html#event-stream-interpretation
 *
 * No I/O, no network, no Vue, no timers — bytes in, frames out.
 * Feed raw network chunks via `feed()` and receive any completed frames.
 * Call `end()` once when the stream closes to flush the UTF-8 decoder.
 */
export class SSEParser {
  private decoder: TextDecoder;
  /** Partial line not yet terminated by \n. */
  private lineBuffer: string;
  /** Accumulates data: lines for the current frame. */
  private dataLines: string[];
  /** Current event-type buffer; resets to '' after dispatch. */
  private eventType: string;
  /**
   * Current id buffer. Persists across frames — never reset after dispatch.
   * `null` means no id has ever been seen.
   */
  private lastId: string | null;

  constructor() {
    this.decoder = new TextDecoder();
    this.lineBuffer = '';
    this.dataLines = [];
    this.eventType = '';
    this.lastId = null;
  }

  /**
   * Feed a raw network chunk.
   * Returns any SSE frames completed by this chunk, in order.
   */
  feed(chunk: Uint8Array): RawEventStreamMessage[] {
    const text = this.decoder.decode(chunk, { stream: true });
    return this._processText(text);
  }

  /**
   * Call once when the stream ends to flush the UTF-8 decoder.
   * Returns [] — a trailing frame with no terminating blank line is discarded per spec.
   */
  end(): RawEventStreamMessage[] {
    // Flush the UTF-8 decoder (handles any remaining multibyte chars)
    const text = this.decoder.decode();
    // Process any remaining text, but per spec an incomplete frame (no blank line) is discarded
    this._processText(text);
    return [];
  }

  private _processText(text: string): RawEventStreamMessage[] {
    const frames: RawEventStreamMessage[] = [];

    // Append decoded text to the running line buffer.
    let combined = this.lineBuffer + text;

    // If combined ends with a \r that is NOT preceded by another \r or \n,
    // hold it back — it may be the first half of a \r\n split across two
    // feed() calls. Re-attach it to lineBuffer so the next feed() sees it
    // together with the following byte:
    //   - next byte is \n  →  forms \r\n  →  single line break (correct)
    //   - next byte is anything else  →  bare \r line-ending (correct)
    // If the trailing \r IS preceded by \r or \n, it cannot be the start of a
    // \r\n pair in a meaningful way and should be processed immediately as a
    // standalone CR line terminator (e.g. the blank line in 'data: x\r\r').
    let trailingCr = '';
    if (combined.endsWith('\r')) {
      const penultimate =
        combined.length >= 2 ? combined[combined.length - 2] : '';
      // Only hold the trailing \r if it is NOT immediately preceded by another
      // \r or \n. When preceded by \r or \n the trailing \r is a blank-line CR
      // (e.g. the second \r in 'data: x\r\r') and must be processed now.
      if (penultimate !== '\r' && penultimate !== '\n') {
        trailingCr = '\r';
        combined = combined.slice(0, -1);
      }
    }

    // Normalise all three line-ending styles to \n so a single split handles all.
    // Replace \r\n first (two-char sequence), then bare \r (anything remaining).
    const normalised = combined.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
    const parts = normalised.split('\n');

    // The last element is either '' (if the text ended with \n) or a partial line.
    // Re-attach any held trailing \r so the next feed() can resolve it.
    this.lineBuffer = (parts.pop() ?? '') + trailingCr;

    for (const line of parts) {
      if (line === '') {
        // Blank line: dispatch the current frame (if data was seen)
        const frame = this._dispatch();
        if (frame !== null) {
          frames.push(frame);
        }
      } else if (line.startsWith(':')) {
        // Comment / heartbeat — ignore
      } else {
        this._processField(line);
      }
    }

    return frames;
  }

  private _processField(line: string): void {
    const colonIdx = line.indexOf(':');

    let field: string;
    let value: string;

    if (colonIdx === -1) {
      // No colon → field name is the whole line, value is ''
      field = line;
      value = '';
    } else {
      field = line.slice(0, colonIdx);
      const rest = line.slice(colonIdx + 1);
      // Strip exactly one leading space from the value, if present
      value = rest.startsWith(' ') ? rest.slice(1) : rest;
    }

    switch (field) {
      case 'data':
        this.dataLines.push(value);
        break;
      case 'event':
        this.eventType = value;
        break;
      case 'id':
        // Ignore if value contains NUL char per spec
        if (!value.includes('\0')) {
          this.lastId = value;
        }
        break;
      case 'retry':
        // Ignored (no reconnection logic in the parser layer)
        break;
      default:
        // Unknown fields are ignored
        break;
    }
  }

  /**
   * Attempt to dispatch the current frame.
   * Returns a `RawEventStreamMessage` only if at least one `data:` line was seen;
   * otherwise returns `null` (per spec: no data = no dispatch).
   * Resets event-type and data buffers; does NOT reset the id buffer.
   */
  private _dispatch(): RawEventStreamMessage | null {
    if (this.dataLines.length === 0) {
      // Per spec: only dispatch if data buffer is non-empty
      this.eventType = '';
      return null;
    }

    const frame: RawEventStreamMessage = {
      id: this.lastId,
      event: this.eventType !== '' ? this.eventType : 'message',
      data: this.dataLines.join('\n'),
    };

    // Reset per-frame buffers; id persists
    this.eventType = '';
    this.dataLines = [];

    return frame;
  }
}
